// State of the in-room "Gravar" control (spec v3.8). The api decides; this
// hook only renders its answers: the token's status, the start response and
// the presence poll (4 s), each allowed to move the state either way. A peer's
// LiveKit data message on RECORDING_DATA_TOPIC is only a hint: it triggers an
// immediate presence fetch and never sets the state itself. Presence answers
// to requests issued before the start response arrived are ignored, so a
// stale poll cannot undo a start it has not seen.
import type { LiveRecordingStatus, RecordingStartResponse } from '@kima-hub/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { userMessage } from '../api/client.js';
import type { ApiClient } from '../api/client.js';
import { usePresenceFeed } from '../presence/usePresence.js';
import type { PresenceOptions } from '../presence/usePresence.js';

export interface RecordingControl {
  status: LiveRecordingStatus;
  pending: boolean;
  /** pt-BR, from a failed request (network, 404, 503); null otherwise. */
  error: string | null;
  /** One request in flight at most: a second press gets the first press's promise. */
  start(): Promise<RecordingStartResponse>;
  /** A peer announced a start on the data channel: refetch presence now. */
  onSignal(): void;
  /** LiveKitRoomView hands over its data-channel sender (null when it unmounts). */
  bindSender(send: (() => void) | null): void;
}

export function useRecordingControl(input: {
  api: Pick<ApiClient, 'startRecording' | 'presence'>;
  meetingId: string;
  slug: string;
  initial: LiveRecordingStatus;
  presence?: PresenceOptions;
}): RecordingControl {
  const { api, meetingId, slug } = input;
  const [status, setStatus] = useState<LiveRecordingStatus>(input.initial);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef<Promise<RecordingStartResponse> | null>(null);
  const sender = useRef<(() => void) | null>(null);
  /** Presence answers from requests numbered up to this predate the last start response. */
  const staleUpTo = useRef(0);
  const presence = usePresenceFeed(api, input.presence);
  const { issued, refresh } = presence;

  useEffect(() => {
    if (presence.state.kind !== 'ok' || inflight.current || presence.seq <= staleUpTo.current) return;
    const recording = presence.state.data.rooms.find((r) => r.slug === slug)?.recording;
    if (recording) setStatus(recording);
  }, [presence.state, presence.seq, slug]);

  const start = useCallback((): Promise<RecordingStartResponse> => {
    if (inflight.current) return inflight.current;
    setPending(true);
    setError(null);
    const request = api.startRecording(meetingId).then(
      (res) => {
        staleUpTo.current = issued();
        setStatus(res.recording);
        if (res.outcome === 'started') sender.current?.();
        return res;
      },
      (err: unknown) => {
        setError(userMessage(err));
        throw err;
      },
    );
    const settled = request.finally(() => {
      inflight.current = null;
      setPending(false);
    });
    inflight.current = settled;
    return settled;
  }, [api, meetingId, issued]);

  const onSignal = useCallback(() => refresh(), [refresh]);
  const bindSender = useCallback((send: (() => void) | null) => {
    sender.current = send;
  }, []);

  return { status, pending, error, start, onSignal, bindSender };
}
